import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '@/app/api';
import { crmBranchPath, crmBranchesPath } from '@crm/crmPaths';
import { bootstrapCrmFromOwner, loginManager } from '@crm/features/auth/crmAuthSlice';
import { UiButton, UiInput, UiLinkButton, UiNativeSelect } from '@shared/common/ui';

export function CrmLoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ownerId: ownerIdInUrl } = useParams();
  const { status, error, sessionChecked, profile } = useSelector((s) => s.crmAuth);
  const ownerAuth = useSelector((s) => s.ownerAuth);
  const ownerBootstrapSent = useRef(false);
  const [publicBranches, setPublicBranches] = useState([]);
  const [branchesLoad, setBranchesLoad] = useState('idle');
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { login: '', password: '', targetBranchId: '' },
  });

  useEffect(() => {
    if (sessionChecked && status === 'authenticated' && profile?.ownerId) {
      navigate(crmBranchesPath(profile.ownerId), { replace: true });
    }
  }, [sessionChecked, status, profile?.ownerId, navigate]);

  useEffect(() => {
    ownerBootstrapSent.current = false;
  }, [ownerIdInUrl]);

  /** Уже вошли как владелец — выдаём CRM-сессию без пароля (ownerId в URL = JWT). */
  useEffect(() => {
    if (!ownerIdInUrl || ownerBootstrapSent.current) return;
    if (!ownerAuth.sessionChecked || !sessionChecked) return;
    if (ownerAuth.status !== 'authenticated') return;
    if (status === 'authenticated' && profile?.ownerId) return;
    if (status === 'loading') return;

    ownerBootstrapSent.current = true;
    dispatch(bootstrapCrmFromOwner({ ownerId: ownerIdInUrl }));
  }, [
    dispatch,
    ownerAuth.sessionChecked,
    ownerAuth.status,
    ownerIdInUrl,
    profile?.ownerId,
    sessionChecked,
    status,
  ]);

  useEffect(() => {
    if (!ownerIdInUrl) {
      setPublicBranches([]);
      setBranchesLoad('idle');
      return;
    }
    let cancelled = false;
    setBranchesLoad('loading');
    (async () => {
      try {
        const { data } = await api.get(`/public/owners/${ownerIdInUrl}/branches`);
        if (cancelled) return;
        const list = data.branches ?? [];
        setPublicBranches(list);
        if (list.length === 1) {
          setValue('targetBranchId', list[0].id);
        }
        setBranchesLoad('succeeded');
      } catch {
        if (!cancelled) {
          setPublicBranches([]);
          setBranchesLoad('failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerIdInUrl, setValue]);

  const targetBranchId = watch('targetBranchId');

  const onSubmit = async (values) => {
    const result = await dispatch(
      loginManager({
        ownerId: ownerIdInUrl,
        login: values.login,
        password: values.password,
      }),
    );
    if (!loginManager.fulfilled.match(result)) return;

    const oid = result.payload?.ownerId;
    const allowed = result.payload?.branches ?? [];
    if (!oid) return;

    const allowedIds = new Set(allowed.map((b) => b.id));
    const chosen = values.targetBranchId?.trim() ?? '';

    if (chosen && !allowedIds.has(chosen)) {
      navigate(crmBranchesPath(oid), { replace: true });
      return;
    }

    if (chosen && allowedIds.has(chosen)) {
      navigate(crmBranchPath(oid, chosen));
      return;
    }

    if (allowed.length === 1) {
      navigate(crmBranchPath(oid, allowed[0].id));
    } else {
      navigate(crmBranchesPath(oid));
    }
  };

  const showBranchSelect = ownerIdInUrl && branchesLoad === 'succeeded' && publicBranches.length > 0;
  const branchRequired = publicBranches.length > 1;

  if (showPasswordRecovery) {
    return (
      <ManagerPasswordRecoveryForm
        ownerId={ownerIdInUrl}
        onBack={() => setShowPasswordRecovery(false)}
      />
    );
  }

  return (
    <div className="auth-card">
      <h1>Вход в CRM</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        {showBranchSelect && (
          <UiNativeSelect
            label="Филиал"
            error={errors.targetBranchId?.message}
            {...register(
              'targetBranchId',
              branchRequired ? { required: 'Выберите филиал' } : {},
            )}
          >
            {publicBranches.length > 1 && <option value="">— выберите филиал —</option>}
            {publicBranches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </UiNativeSelect>
        )}
        {ownerIdInUrl && branchesLoad === 'failed' && (
          <p className="muted">
            Не удалось загрузить список филиалов. Войдите по логину — после входа выберите филиал.
          </p>
        )}
        {ownerIdInUrl && branchesLoad === 'succeeded' && publicBranches.length === 0 && (
          <p className="muted">У этой организации пока нет филиалов в системе.</p>
        )}
        <UiInput
          label="Логин"
          autoComplete="username"
          error={errors.login?.message}
          {...register('login', { required: 'Введите логин' })}
        />
        <UiInput
          label="Пароль"
          type="password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password', { required: 'Введите пароль' })}
        />
        <div style={{ marginTop: -8, marginBottom: 12, textAlign: 'right' }}>
          <UiLinkButton type="button" variant="link" onClick={() => setShowPasswordRecovery(true)}>
            Забыли пароль?
          </UiLinkButton>
        </div>
        {error && <span className="form-error">{error}</span>}
        <UiButton
          
          type="submit"
          disabled={
            isSubmitting ||
            status === 'loading' ||
            (branchRequired && !targetBranchId)
          }
        >
          {isSubmitting || status === 'loading' ? 'Входим…' : 'Войти'}
        </UiButton>
      </form>
    </div>
  );
}

function ManagerPasswordRecoveryForm({ ownerId, onBack }) {
  const [infoMessage, setInfoMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { email: '' } });

  const onSubmit = async ({ email }) => {
    setInfoMessage(null);
    setErrorMessage(null);
    try {
      const { data } = await api.post('/auth/crm/forgot', { ownerId, email });
      setInfoMessage(data.message);
    } catch (err) {
      setErrorMessage(err.response?.data?.message ?? 'Не удалось отправить инструкцию');
    }
  };

  return (
    <div className="auth-card">
      <h1>Восстановление пароля CRM</h1>
      <p className="muted">Укажите email, который используется как логин в CRM.</p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <UiInput
          label="Email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email', {
            required: 'Введите email',
            pattern: { value: /\S+@\S+\.\S+/, message: 'Некорректный email' },
          })}
        />
        {errorMessage && <span className="form-error">{errorMessage}</span>}
        {infoMessage && <div className="banner banner--info">{infoMessage}</div>}
        <UiButton type="submit" disabled={isSubmitting || Boolean(infoMessage)}>
          {isSubmitting ? 'Отправляем…' : 'Отправить инструкцию'}
        </UiButton>
      </form>
      <div style={{ marginTop: 16 }}>
        <UiLinkButton type="button" variant="link" onClick={onBack}>
          Вернуться ко входу
        </UiLinkButton>
      </div>
    </div>
  );
}
